<p-table
    [value]="value()"
    [loading]="loading()"
    [size]="size()"
    [styleClass]="styleClass()"
    [showGridlines]="showGridlines()"
    [stripedRows]="stripedRows()"
    [responsiveLayout]="responsiveLayout()"
    [tableStyle]="tableStyle()"
    [tableStyleClass]="tableStyleClass()"
    [scrollable]="scrollable()"
    [scrollHeight]="scrollHeight()"
    [virtualScroll]="virtualScroll()"
    [virtualScrollItemSize]="virtualScrollItemSize()"
    [virtualScrollDelay]="virtualScrollDelay"
    [virtualScrollOptions]="virtualScroll() ? virtualScrollOptions : undefined"
    [selectionMode]="selectionModeForTable()"
    [selection]="selection()"
    [dataKey]="dataKey()"
    [metaKeySelection]="metaKeySelection()"
    [sortMode]="sortMode()"
    [sortField]="resolvedSortField()"
    [sortOrder]="resolvedSortOrder()"
    [multiSortMeta]="multiSortMeta()"
    [paginator]="paginator()"
    [rows]="rows()"
    [totalRecords]="totalRecordsForTable()"
    [first]="first()"
    [pageLinks]="pageLinks()"
    [showFirstLastIcon]="showFirstLastIcon()"
    [showPageLinks]="showPageLinks()"
    [showJumpToPageDropdown]="showJumpToPageDropdown()"
    [showCurrentPageReport]="showCurrentPageReport()"
    [currentPageReportTemplate]="currentPageReportTemplate()"
    [lazy]="lazy()"
    [lazyLoadOnInit]="lazyLoadOnInit()"
    (onRowSelect)="onRowSelect($event)"
    (onRowUnselect)="onRowUnselect($event)"
    (onSort)="onSort($event)"
    (selectionChange)="onSelectionChange($event)"
    (onPage)="onPage($event)"
    (onLazyLoad)="onLazyLoad($event)"
>
    @if (hasCustomCaption()) {
        <ng-template #caption>
            <ng-container [ngTemplateOutlet]="captionTemplate()"></ng-container>
        </ng-template>
    }

    @if (hasCustomHeader()) {
        <ng-template #header>
            <ng-container [ngTemplateOutlet]="headerTemplate()"></ng-container>
        </ng-template>
    } @else if (shouldShowDefaultHeader()) {
        <ng-template #header>
            <tr>
                @if (selectionMode() === selectionModeEnum.MultipleCheckbox) {
                    <th>
                        <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
                    </th>
                }
                @for (column of columns(); track column.field) {
                    <th
                        [style]="column.headerStyle"
                        [class]="column.styleClass"
                        [style.width]="column.width"
                        [style.max-width]="column.width"
                        [pSortableColumn]="column.sortable ? column.field : null"
                        [pSortableColumnDisabled]="!column.sortable"
                    >
                        {{ column.header }}
                        @if (column.sortable) {
                            <span
                                class="fc-font-icon"
                                [class.fc-font-icon-arrow-small-up]="
                                    resolvedSortField() === column.field && resolvedSortOrder() === sortOrderAscending
                                "
                                [class.fc-font-icon-arrow-small-down]="
                                    resolvedSortField() === column.field && resolvedSortOrder() === sortOrderDescending
                                "
                                [class.fc-font-icon-chevron-filled-up-down]="resolvedSortField() !== column.field"
                            ></span>
                        }
                    </th>
                }
            </tr>
        </ng-template>
    }

    @if (hasCustomBody()) {
        <ng-template #body let-rowData let-rowIndex="rowIndex">
            <ng-container
                [ngTemplateOutlet]="bodyTemplate()"
                [ngTemplateOutletContext]="{ $implicit: rowData, rowIndex: rowIndex }"
            >
            </ng-container>
        </ng-template>
    } @else if (shouldShowDefaultBody()) {
        <ng-template #body let-rowData let-rowIndex="rowIndex">
            <tr
                [pSelectableRow]="rowData"
                [pSelectableRowIndex]="rowIndex"
                [style.height]="virtualScroll() ? virtualScrollItemSize() + 'px' : null"
            >
                @if (rowData) {
                    @if (selectionMode() === selectionModeEnum.MultipleCheckbox) {
                        <td>
                            <p-tableCheckbox [value]="rowData"></p-tableCheckbox>
                        </td>
                    }
                    @for (column of columns(); track column.field) {
                        <td
                            [style]="column.bodyStyle"
                            [class]="column.styleClass"
                            [style.width]="column.width"
                            [style.max-width]="column.width"
                        >
                            @if (column.cellTemplate) {
                                <ng-container
                                    [ngTemplateOutlet]="column.cellTemplate"
                                    [ngTemplateOutletContext]="{
                                        $implicit: rowData[column.field],
                                        value: rowData[column.field],
                                        rowData: rowData,
                                        column: column,
                                        rowIndex: rowIndex
                                    }"
                                >
                                </ng-container>
                            } @else {
                                {{ rowData[column.field] }}
                            }
                        </td>
                    }
                }
            </tr>
        </ng-template>

        <!-- Shown by PrimeNG for unloaded rows when virtualScroll + lazy are enabled -->
        <ng-template #loadingbody>
            <!-- Set the height of a row to use in calculations of virtual scrolling -->
            <tr [style.height]="`${virtualScrollItemSize()}px`">
                @if (selectionMode() === selectionModeEnum.MultipleCheckbox) {
                    <td>
                        <p-skeleton />
                    </td>
                }
                @for (column of columns(); track column.field; let even = $even) {
                    <td
                        [style]="column.bodyStyle"
                        [class]="column.styleClass"
                        [style.width]="column.width"
                        [style.max-width]="column.width"
                    >
                        <p-skeleton />
                    </td>
                }
            </tr>
        </ng-template>
    }

    @if (hasCustomFooter()) {
        <ng-template #footer>
            <tr>
                <td
                    [attr.colspan]="
                        (columns()?.length ?? 0) + (selectionMode() === selectionModeEnum.MultipleCheckbox ? 1 : 0)
                    "
                >
                    <ng-container [ngTemplateOutlet]="footerTemplate()"></ng-container>
                </td>
            </tr>
        </ng-template>
    }

    @if (hasCustomEmpty()) {
        <ng-template #emptymessage>
            <ng-container [ngTemplateOutlet]="emptyTemplate()"></ng-container>
        </ng-template>
    }

    <ng-content></ng-content>
</p-table>
